import {useTranslation} from '@aboutyou/i18n';
import React, {FC} from 'react';
import styled from 'styled-components';
import {COLORS} from '../../../../../constants/colors';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../../../constants/typography';
import {FilterCard} from '../../../../atoms/FilterCard/FilterCard';
import {Props as ImageIconProps} from '../../../../atoms/Image/ImageIcon';
import {FilterFlyout} from '../FilterFlyout';
import {
  Props as FilterDropdownBooleanType,
  FilterFlyoutBoolean,
  Label as FilterFlyoutBooleanLabel,
} from '../FilterFlyoutBoolean/FilterFlyoutBoolean';
import {FilterFlyoutFooter} from '../FilterFlyoutFooter';

interface Props {
  label: string;
  onToggleFilterContent: (slug: string, isActive: boolean) => void;
  booleanFilters: Array<{
    filter?: Omit<FilterDropdownBooleanType, 'onToggle'> & {
      slug: string;
      icon?: ImageIconProps;
    };
  }>;
  dataTestId?: string;
  open: () => void;
  hide: () => void;
  isOpen: boolean;
  className?: string;
  enableFlyoutFilter: boolean;
}

export const FilterFlyoutMultipleBoolean: FC<Props> = ({
  booleanFilters,
  label,
  onToggleFilterContent,
  dataTestId = 'filterFlyoutMultipleBoolean',
  open,
  hide,
  isOpen,
  className,
  enableFlyoutFilter,
}) => {
  const {t} = useTranslation();
  const activeFilters = booleanFilters.filter(filter => filter.filter?.active);

  return (
    <section data-testid={dataTestId}>
      <FilterCard
        count={activeFilters.length}
        title={label}
        onClick={open}
        disableHorizontalPadding={enableFlyoutFilter}
      />

      <FilterFlyout
        data-testid="filterFlyoutSectionSizeGrouped_filterFlyout"
        onClose={hide}
        open={isOpen}
        title={label}
        primaryButtonLabel={t('Submit')}
        secondaryButtonLabel={t('Back')}
        footer={FilterFlyoutFooter}
      >
        <Wrapper className={className} $maxHeight={enableFlyoutFilter}>
          {booleanFilters.map(filter => {
            if (!filter.filter) {
              return null;
            }

            const {active, label, sublabel, slug, icon} = filter.filter;

            return (
              <StyledFilterFlyoutBoolean
                key={`${dataTestId}-${slug}`}
                active={active}
                label={label}
                sublabel={sublabel}
                icon={icon}
                onToggle={() => onToggleFilterContent(slug, active)}
              />
            );
          })}
        </Wrapper>
      </FilterFlyout>
    </section>
  );
};

const Wrapper = styled.div<{$maxHeight?: boolean}>`
  display: flex;
  flex-direction: column;
  background-color: ${COLORS.White};
  ${props => (props.$maxHeight ? 'height: 100%;' : '')}
`;

const StyledFilterFlyoutBoolean = styled(FilterFlyoutBoolean)`
  align-items: flex-start;
  border: 0;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  height: auto;
  padding: 1rem 0;

  &:not(:last-child) {
    border-bottom: solid 1px ${COLORS.Grey3};
  }

  ${FilterFlyoutBooleanLabel} {
    color: ${COLORS.Black};
    justify-content: space-between;
    width: 100%;

    font-size: ${FONT_SIZES.px14};
    font-weight: ${FONT_WEIGHTS.light};
  }
`;
